/**
 * Structured representation of a content control tag.
 * All fields are optional; only present fields are included when serialized.
 */
export interface ContentControlTag {
  /** Parameter names bound to this control. Serialized as `param:name=%name%` per entry. */
  params?: string[];
  /** Whether the control has selection-mode enabled. Serialized as `selection:1`. */
  selection?: boolean;
  /** XPath object path. Serialized as `objpath:value`. */
  objpath?: string;
  /** Report file path. Serialized as `path:value`. */
  path?: string;
  /** Report selector provider. Serialized as `selector:value`. */
  selector?: string;
  /** Raw unrecognized entries (e.g. a plain property key). Serialized as-is. */
  extra?: string[];
}

/**
 * Parses a raw semicolon-delimited tag string into a typed ContentControlTag object.
 */
export function parseContentControlTag(raw: string | undefined): ContentControlTag {
  const result: ContentControlTag = {};
  if (!raw) {
    return result;
  }

  for (const part of raw.split(';')) {
    if (!part) {
      continue;
    }

    if (part === 'selection:1') {
      result.selection = true;
    } else if (part.startsWith('param:')) {
      const eqIdx = part.indexOf('=');
      const name = eqIdx !== -1 ? part.slice('param:'.length, eqIdx) : part.slice('param:'.length);
      if (name) {
        (result.params ??= []).push(name);
      }
    } else if (part.startsWith('objpath:')) {
      result.objpath = part.slice('objpath:'.length);
    } else if (part.startsWith('path:')) {
      result.path = part.slice('path:'.length);
    } else if (part.startsWith('selector:')) {
      result.selector = part.slice('selector:'.length);
    } else {
      (result.extra ??= []).push(part);
    }
  }

  return result;
}

/**
 * Serializes a ContentControlTag object into a semicolon-delimited tag string.
 */
export function serializeContentControlTag(tag: ContentControlTag): string {
  const parts: string[] = [];

  if (tag.extra) {
    parts.push(...tag.extra);
  }
  if (tag.objpath !== undefined) {
    parts.push(`objpath:${tag.objpath}`);
  }
  if (tag.path !== undefined) {
    parts.push(`path:${tag.path}`);
  }
  if (tag.selector !== undefined) {
    parts.push(`selector:${tag.selector}`);
  }
  if (tag.selection) {
    parts.push('selection:1');
  }
  if (tag.params) {
    for (const name of tag.params) {
      parts.push(`param:${name}=%${name}%`);
    }
  }

  return parts.join(';');
}

/**
 * Options for creating a content control
 */
export interface ContentControlOptions {
  title?: string;
  tag?: ContentControlTag;
  placeholder?: string;
  cannotEdit?: boolean;
  cannotDelete?: boolean;
  color?: string;
}

/**
 * Configures common properties of a content control
 * @param contentControl - The content control to configure
 * @param options - Configuration options
 */
function configureContentControl(
  contentControl: Word.ContentControl,
  options: ContentControlOptions
): void {
  if (options.title) {
    contentControl.title = options.title;
  }

  if (options.tag) {
    contentControl.tag = serializeContentControlTag(options.tag);
  }

  if (options.placeholder) {
    contentControl.placeholderText = options.placeholder;
  }

  if (options.cannotEdit !== undefined) {
    contentControl.cannotEdit = options.cannotEdit;
  }

  if (options.cannotDelete !== undefined) {
    contentControl.cannotDelete = options.cannotDelete;
  }

  if (options.color) {
    contentControl.color = options.color;
  }
}

/**
 * Extracts parameter names from a collection of loaded custom property items.
 * Handles wizard (Wizard_ prefix), selector (Selector_ prefix) and fixed (Const_ prefix) modes.
 * Properties without one of these prefixes are foreign document properties (officeatwork,
 * SharePoint, ...) and are never treated as parameters.
 */
function buildParamNamesFromItems(items: { key: string }[]): string[] {
  const paramNames = new Set<string>();
  for (const prop of items) {
    if (prop.key.startsWith('Wizard_')) {
      let name = prop.key.substring('Wizard_'.length);
      if (name.endsWith('.Order')) {
        name = name.substring(0, name.length - '.Order'.length);
      } else if (name.endsWith('.Default')) {
        name = name.substring(0, name.length - '.Default'.length);
      } else if (name.endsWith('.Name')) {
        name = name.substring(0, name.length - '.Name'.length);
      }
      if (name) {
        paramNames.add(name);
      }
    } else if (prop.key.startsWith('Selector_')) {
      let name = prop.key.substring('Selector_'.length);
      if (name.endsWith('.Key')) {
        name = name.substring(0, name.length - '.Key'.length);
      }
      if (name) {
        paramNames.add(name);
      }
    } else if (prop.key.startsWith('Const_')) {
      const name = prop.key.substring('Const_'.length);
      if (name) {
        paramNames.add(name);
      }
    }
  }
  return Array.from(paramNames);
}

export interface CustomProperty {
  key: string;
  value: string;
  type: string;
}

/**
 * Gets all custom properties from the document
 * @returns Promise that resolves with an array of custom properties
 */
export async function getCustomProperties(): Promise<CustomProperty[]> {
  return Word.run(async (context) => {
    const customProperties = context.document.properties.customProperties;
    customProperties.load('items');
    await context.sync();

    const properties: CustomProperty[] = [];

    for (const prop of customProperties.items) {
      prop.load('key,value,type');
    }
    await context.sync();

    for (const prop of customProperties.items) {
      properties.push({
        key: prop.key,
        value: String(prop.value),
        type: prop.type
      });
    }

    return properties;
  });
}

/**
 * Gets all parameter names from the document's custom properties.
 * A parameter can be in wizard mode (Wizard_ prefix), selector mode (Selector_ prefix),
 * or fixed mode (Const_ prefix).
 */
export async function getExistingParamNames(): Promise<string[]> {
  return Word.run(async (context) => {
    const customProperties = context.document.properties.customProperties;
    customProperties.load('items');
    await context.sync();
    for (const prop of customProperties.items) {
      prop.load('key');
    }
    await context.sync();
    return buildParamNamesFromItems(customProperties.items);
  });
}

/**
 * Inserts a rich text content control at the current selection
 * @param options - Configuration options for the content control
 * @returns Promise that resolves when the content control is created
 */
export async function insertRichTextControl(
  options: ContentControlOptions
): Promise<Word.ContentControl> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    const selectionRange = selection.getRange();

    // Check if the selection is within a table content control
    const parentContentControl = selectionRange.parentContentControlOrNullObject;
    parentContentControl.load(['isNullObject', 'title', 'tag']);
    const customPropertiesCollection = context.document.properties.customProperties;
    customPropertiesCollection.load('items');
    await context.sync();

    let parentContentControlTitle: string | null = null;
    let parentContentControlTag: string | null = null;
    if (!parentContentControl.isNullObject) {
      parentContentControlTitle = parentContentControl.title;
      parentContentControlTag = parentContentControl.tag;
    }

    // Transform title if inserting into a table content control
    const transformedOptions = { ...options };
    if (parentContentControlTitle && transformedOptions.title?.startsWith('CMI_')) {
      // Special case: If parent is CMI_XPathList, check if tags match
      if (
        parentContentControlTitle === 'CMI_XPathList' &&
        parentContentControlTag &&
        transformedOptions.tag
      ) {
        // Check if the tags match (parent objpath should be a prefix of the child objpath)
        const parentTagParsed = parseContentControlTag(parentContentControlTag);
        if (
          parentTagParsed.objpath &&
          transformedOptions.tag.objpath?.startsWith(parentTagParsed.objpath)
        ) {
          // Replace CMI_XPathObj. with ITEM.
          if (transformedOptions.title.startsWith('CMI_XPathObj.')) {
            transformedOptions.title =
              'ITEM.' + transformedOptions.title.substring('CMI_XPathObj.'.length);
          }
        }
      } else if (
        // Provider-level titles (CMI_Betreff, no .property part) are never list items,
        // so they must keep their title instead of being rewritten to ITEM.
        transformedOptions.title.includes('.') &&
        transformedOptions.title.startsWith(parentContentControlTitle)
      ) {
        // The new control's title starts with the parent control's title
        // Replace the parent control's title prefix with ITEM
        if (transformedOptions.title === parentContentControlTitle) {
          // Exact match: CMI_Bezugspersonen -> ITEM
          transformedOptions.title = 'ITEM';
        } else {
          // Has additional parts: CMI_Bezugspersonen.Name -> ITEM.Name
          transformedOptions.title =
            'ITEM' + transformedOptions.title.substring(parentContentControlTitle.length);
        }
      }
      // Otherwise, keep the original title (different provider)
    }

    for (const prop of customPropertiesCollection.items) {
      prop.load('key');
    }
    selection.load(['font/color', 'font/size', 'font/name', 'font/bold', 'font/italic', 'style']);
    await context.sync();

    const existingParamNames = buildParamNamesFromItems(customPropertiesCollection.items);

    const contentControl = selection.insertContentControl(Word.ContentControlType.richText);
    contentControl.font.name = selection.font.name;
    contentControl.font.size = selection.font.size;
    contentControl.font.bold = selection.font.bold;
    contentControl.font.italic = selection.font.italic;
    contentControl.font.color = selection.font.color;

    // Append existing param entries to the tag of the new content control
    // Skip if the title starts with CMR_ (report controls) or ITEM (list item controls)
    if (
      existingParamNames.length > 0 &&
      !transformedOptions.title?.startsWith('CMR_') &&
      !transformedOptions.title?.startsWith('ITEM')
    ) {
      const existingParams = transformedOptions.tag?.params ?? [];
      for (const name of existingParamNames) {
        if (!existingParams.includes(name)) {
          existingParams.push(name);
        }
      }
      transformedOptions.tag = { ...transformedOptions.tag, params: existingParams };
    }

    configureContentControl(contentControl, transformedOptions);

    // Place the caret AFTER the newly inserted control so the control appears to the left
    // (matching how a typed character behaves)
    try {
      const afterRange = contentControl.getRange(Word.RangeLocation.after);
      afterRange.select(Word.SelectionMode.select);
    } catch {
      // If for some reason getRange isn't available, fall back to selecting the end
      // which at least moves the caret near the control.
      contentControl.select(Word.SelectionMode.end);
    }

    await context.sync();

    // Remove focus from the button to prevent accidental inserts via spacebar
    (document.activeElement as HTMLElement).blur();

    return contentControl;
  });
}

/**
 * Inserts a table wrapped in a content control at the current selection.
 * If a table is already selected, wraps that table with a content control instead of inserting a new one.
 * @param options - Configuration options for the content control
 * @param rowCount - Number of rows in the table (default: 2) - only used when inserting a new table
 * @param columnCount - Number of columns in the table (default: 2) - only used when inserting a new table
 * @returns Promise that resolves when the content control is created
 */
export async function insertTableControl(
  options: ContentControlOptions,
  rowCount = 2,
  columnCount = 2
): Promise<Word.ContentControl> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    const selectionRange = selection.getRange();

    // Check if the selection is within a table
    const existingTable = selectionRange.tables.getFirstOrNullObject();
    const customPropertiesCollection = context.document.properties.customProperties;
    customPropertiesCollection.load('items');
    await context.sync();

    for (const prop of customPropertiesCollection.items) {
      prop.load('key');
    }

    let tableRange: Word.Range;

    if (!existingTable.isNullObject) {
      // If a table is selected, use its range
      tableRange = existingTable.getRange();
      await context.sync();
    } else {
      // If no table is selected, insert a new table
      const table = selection.insertTable(rowCount, columnCount, Word.InsertLocation.after);
      await context.sync();
      tableRange = table.getRange();
    }

    const existingParamNames = buildParamNamesFromItems(customPropertiesCollection.items);

    // Wrap the table range with a content control
    const contentControl = tableRange.insertContentControl(Word.ContentControlType.richText);

    // Append existing param entries to the tag of the new content control
    // Skip if the title starts with CMR_ (report controls)
    const tableOptions = { ...options };
    if (existingParamNames.length > 0 && !tableOptions.title?.startsWith('CMR_')) {
      const existingParams = tableOptions.tag?.params ?? [];
      for (const name of existingParamNames) {
        if (!existingParams.includes(name)) {
          existingParams.push(name);
        }
      }
      tableOptions.tag = { ...tableOptions.tag, params: existingParams };
    }

    configureContentControl(contentControl, tableOptions);

    // Position caret after the wrapped/inserted table control for intuitive typing flow
    try {
      const afterRange = contentControl.getRange(Word.RangeLocation.after);
      afterRange.select(Word.SelectionMode.select);
    } catch {
      contentControl.select(Word.SelectionMode.end);
    }

    await context.sync();

    // Remove focus from the button to prevent accidental inserts via spacebar
    (document.activeElement as HTMLElement).blur();

    return contentControl;
  });
}

export function writeProperty(propertyKey: string, value: string): Promise<void> {
  return Word.run(async (context) => {
    const customProperties = context.document.properties.customProperties;
    customProperties.add(propertyKey, value);
    await context.sync();
  });
}

export function writePropertyTyped(
  propertyKey: string,
  value: number | boolean | Date
): Promise<void> {
  return Word.run(async (context) => {
    const customProperties = context.document.properties.customProperties;
    customProperties.add(propertyKey, value);
    await context.sync();
  });
}

export function deleteProperty(propertyKey: string): Promise<void> {
  return Word.run(async (context) => {
    const customProperties = context.document.properties.customProperties;
    const property = customProperties.getItemOrNullObject(propertyKey);

    property.delete();
    await context.sync();
  });
}

/**
 * Builds a stable selector key for report content controls.
 * The key is used in tags (`selector:<key>`) and document properties (`Selector_<key>`).
 */
export function buildReportSelectorKey(rawValue: string): string {
  const normalized = rawValue
    .trim()
    .replace(/[^A-Za-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalized || 'ReportSelector';
}

/**
 * Ensures report selector document properties exist and are mapped to a provider.
 */
export async function ensureReportSelectorProperties(
  selectorKey: string,
  providerKey: string
): Promise<void> {
  const baseKey = `Selector_${selectorKey}`;
  await writeProperty(baseKey, '');
  await writeProperty(`${baseKey}.Key`, providerKey);
}

/**
 * Removes report selector document properties when no report control references them anymore.
 */
export async function removeReportSelectorPropertiesIfUnused(
  selectorKey: string,
  excludedContentControlId?: number
): Promise<void> {
  const isStillUsed = await Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load('items');
    await context.sync();

    for (const control of contentControls.items) {
      control.load(['id', 'title', 'tag']);
    }
    await context.sync();

    return contentControls.items.some((control) => {
      if (control.id === excludedContentControlId) {
        return false;
      }
      if (!control.title?.startsWith('CMR_')) {
        return false;
      }
      return parseContentControlTag(control.tag).selector === selectorKey;
    });
  });

  if (isStillUsed) {
    return;
  }

  const baseKey = `Selector_${selectorKey}`;
  await Word.run(async (context) => {
    const customProperties = context.document.properties.customProperties;
    customProperties.getItemOrNullObject(baseKey).delete();
    customProperties.getItemOrNullObject(`${baseKey}.Key`).delete();
    customProperties.getItemOrNullObject(`${baseKey}.WizardData`).delete();
    await context.sync();
  });
}

export function getProperty(propertyKey: string): Promise<CustomProperty | null> {
  return Word.run(async (context) => {
    const customProperties = context.document.properties.customProperties;
    const property = customProperties.getItemOrNullObject(propertyKey);
    property.load();
    await context.sync();

    if (property.isNullObject) {
      return null;
    }

    const rawValue: boolean | Date | number | string = property.value as
      boolean | Date | number | string;
    let value: string;
    if (rawValue instanceof Date) {
      const year = rawValue.getUTCFullYear();
      const month = String(rawValue.getUTCMonth() + 1).padStart(2, '0');
      const day = String(rawValue.getUTCDate()).padStart(2, '0');
      value = `${year}-${month}-${day}`;
    } else {
      value = String(rawValue);
    }

    return {
      key: property.key,
      value,
      type: property.type
    } satisfies CustomProperty;
  });
}

/**
 * Information about a content control in the document
 */
export interface ContentControlInfo {
  id: number;
  title: string;
  tag: string;
  text: string;
  type: string;
  cannotEdit: boolean;
  cannotDelete: boolean;
}

/**
 * Gets all content controls from the document
 * @returns Promise that resolves with an array of content control information
 */
export async function getAllContentControls(): Promise<ContentControlInfo[]> {
  return Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load('items');
    await context.sync();

    const controls: ContentControlInfo[] = [];

    for (const control of contentControls.items) {
      control.load(['id', 'title', 'tag', 'text', 'type', 'cannotEdit', 'cannotDelete']);
    }

    await context.sync();

    for (const control of contentControls.items) {
      controls.push({
        id: control.id,
        title: control.title,
        tag: control.tag,
        text: control.text,
        type: control.type,
        cannotEdit: control.cannotEdit,
        cannotDelete: control.cannotDelete
      });
    }

    return controls;
  });
}

/**
 * Selects a content control by ID in the Word document
 * @param id - The ID of the content control to select
 * @returns Promise that resolves when the content control is selected
 */
export async function selectContentControl(id: number): Promise<void> {
  return Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load('items');
    await context.sync();

    for (const control of contentControls.items) {
      control.load('id');
      await context.sync();

      if (control.id === id) {
        control.select('Select');
        await context.sync();
        return;
      }
    }

    throw new Error(`Content control with ID ${id} not found`);
  });
}

export async function updateContentControl(
  id: number,
  title: string,
  tag: string,
  placeholder?: string
): Promise<void> {
  return Word.run(async (context) => {
    const contentControl = context.document.contentControls.getByIdOrNullObject(id);
    contentControl.load('isNullObject');
    await context.sync();

    if (contentControl.isNullObject) {
      return;
    }

    contentControl.title = title;
    contentControl.tag = tag;
    if (placeholder !== undefined) {
      contentControl.placeholderText = placeholder;
    }
    await context.sync();
  });
}

/**
 * Information about table options for a content control
 */
export interface TableOptionsData {
  title: string;
  tag: string;
  displayTitle: string;
  selectionMode: 'withSelection' | 'withoutSelection';
}

/**
 * Gets table options data for a content control
 * @param contentControlId - The ID of the content control
 * @returns Promise that resolves with the table options data, or null if not found
 */
export async function getTableOptionsData(
  contentControlId: number
): Promise<TableOptionsData | null> {
  return Word.run(async (context) => {
    const contentControl = context.document.contentControls.getByIdOrNullObject(contentControlId);
    contentControl.load(['title', 'tag', 'isNullObject']);
    await context.sync();

    if (contentControl.isNullObject) {
      return null;
    }

    const title = contentControl.title;
    const tag = contentControl.tag;

    // Extract display title
    const titleWithoutPrefix = title.startsWith('CMI_') ? title.substring(4) : title;
    const parts = titleWithoutPrefix.split(/[.:]/);
    const displayTitle = parts[0] || titleWithoutPrefix;

    // Set selection mode based on tag — parsed from the structured tag object
    const selectionMode = parseContentControlTag(tag).selection
      ? 'withSelection'
      : 'withoutSelection';

    return {
      title,
      tag,
      displayTitle,
      selectionMode
    };
  });
}

/**
 * Appends a param:name entry to the tag of every content control in the document.
 * If a control's tag already contains the entry it is left unchanged (idempotent).
 * Multiple entries are separated by semicolons.
 * @param propertyName - The document-property name to encode as `param:<propertyName>=%<propertyName>%`
 */
export async function appendParamToAllContentControlTags(propertyName: string): Promise<void> {
  return Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load('items');
    await context.sync();

    for (const control of contentControls.items) {
      control.load('tag,title');
    }
    await context.sync();

    for (const control of contentControls.items) {
      if (control.title?.startsWith('CMR_')) {
        continue;
      }
      const tagObj = parseContentControlTag(control.tag);
      const params = tagObj.params ?? [];
      if (!params.includes(propertyName)) {
        params.push(propertyName);
        control.tag = serializeContentControlTag({ ...tagObj, params });
      }
    }

    await context.sync();
  });
}

/**
 * Removes a param:name entry from the tag of every content control in the document.
 * If a control's tag does not contain the entry it is left unchanged (idempotent).
 * @param propertyName - The document-property name encoded as `param:<propertyName>=%<propertyName>%`
 */
export async function removeParamFromAllContentControlTags(propertyName: string): Promise<void> {
  return Word.run(async (context) => {
    const contentControls = context.document.contentControls;
    contentControls.load('items');
    await context.sync();

    for (const control of contentControls.items) {
      control.load('tag,title');
    }
    await context.sync();

    for (const control of contentControls.items) {
      if (control.title?.startsWith('CMR_')) {
        continue;
      }
      const tagObj = parseContentControlTag(control.tag);
      const params = tagObj.params ?? [];
      const index = params.indexOf(propertyName);
      if (index !== -1) {
        control.tag = serializeContentControlTag({
          ...tagObj,
          params: params.filter((_, i) => i !== index)
        });
      }
    }

    await context.sync();
  });
}

/**
 * Updates the tag of a content control based on selection mode
 * @param contentControlId - The ID of the content control
 * @param selectionMode - The selection mode to apply
 * @returns Promise that resolves with the updated tag value, or null if not found
 */
export async function updateTableOptionsTag(
  contentControlId: number,
  selectionMode: 'withSelection' | 'withoutSelection'
): Promise<string | null> {
  return Word.run(async (context) => {
    const contentControl = context.document.contentControls.getByIdOrNullObject(contentControlId);
    contentControl.load(['tag', 'isNullObject']);
    await context.sync();

    if (contentControl.isNullObject) {
      return null;
    }

    const tagObj = parseContentControlTag(contentControl.tag);
    tagObj.selection = selectionMode === 'withSelection' ? true : undefined;
    contentControl.tag = serializeContentControlTag(tagObj);

    await context.sync();

    return contentControl.tag;
  });
}
